import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import PropTypes from 'prop-types';
import { Component, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { withRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import ReactTooltip from 'react-tooltip';
import ClipRecording from '~/components/ClipRecording';
import ConfirmationModal from '~/components/ConfirmationModal';
import DownloadButtonPicker from '~/components/DownloadButton/DownloadButtonPicker';
import ShareButton from '~/components/ShareButton';
import { DEFAULT_STREAM_DESCRIPTION } from '~/helpers/constants';
import decodeURIComponentSafe from '~/helpers/decodeURIComponentSafe';
import { RecordingSource } from '~/helpers/enums';
import {
  broadcastStreamShape,
  heroStreamShape,
  remoteStreamShape,
  routerLocationShape,
} from '~/helpers/propTypeShapes';
import roundRecordingDuration from '~/helpers/roundRecordingDuration';
import useDisableVideoDownload from '~/helpers/useDisableVideoDownload';
import '~/style/broadcaststreams.css';

const customClippingStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    maxWidth: '600px',
    padding: '0',
    textAlign: 'left',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.5)',
  },
};

const StreamListItem = ({
  stream,
  streamToken,
  newBroadcastTitle,
  isCurrentUserStreamOwner,
  handleClippingRequest,
  handleDescriptionChange,
  handleEnterKey,
  requestDeleteRecording,
  index,
  broadcastHash,
  broadcastId,
}) => {
  const [userRoleType] = useState(isCurrentUserStreamOwner ? '(host)' : '(guest)');
  const [title, setTitle] = useState();

  useEffect(
    () => setTitle(stream?.title ? stream.title : `${stream.user.name} ${userRoleType}`),
    [stream, userRoleType]
  );

  const originalDescription = useMemo(() => {
    const desc = stream.description ? decodeURIComponentSafe(stream.description) : DEFAULT_STREAM_DESCRIPTION;
    return desc;
  }, [stream.description]);

  const [streamDescription, setStreamDescription] = useState(() => {
    const desc = stream.description ? decodeURIComponentSafe(stream.description) : DEFAULT_STREAM_DESCRIPTION;
    return desc;
  });
  const [displayCheckMark, setDisplayCheckMark] = useState(false);
  const [showDuration, setShowDuration] = useState(true);
  const { disableRightClickFunc } = useDisableVideoDownload();
  const playerRef = useRef();

  const pagePath = `/broadcastdetails/broadcast/${broadcastHash}/${broadcastId}/streams`;
  const thumbnail = stream.thumbnail !== null ? stream.thumbnail : '/FPO_video_thumb.png';

  const isClippable = stream.outfile?.endsWith('mp4') ?? false;
  let allowContentEditing;
  let clippable = '';
  let deleteTooltipCopy;
  let forbidDeletion;
  let sourceURL;
  let sourceType;

  const handleOnClickToPlay = (event) => {
    const { videojs } = window;

    if (!playerRef.current) {
      const player = event.target;

      playerRef.current = videojs(player, { controls: true }, () => {
        player.play();
        setShowDuration(false);
      });
    }

    const isVideoPaused = playerRef.current.paused();
    setShowDuration(isVideoPaused);
  };

  const onHandleDescriptionChange = async (event) => {
    const newDescriptionValue = event.target.innerText;
    const streamId = stream.id;

    if (newDescriptionValue !== streamDescription) {
      const { error } = await handleDescriptionChange(newDescriptionValue, streamId);
      if (error) {
        setStreamDescription(originalDescription);
        return;
      }
      setDisplayCheckMark(true);
    }

    const desc = newDescriptionValue === '' ? DEFAULT_STREAM_DESCRIPTION : newDescriptionValue;
    setStreamDescription(desc);
  };

  const handleOnClick = (event) => {
    const currentDescription = event.target.innerText;
    if (currentDescription === DEFAULT_STREAM_DESCRIPTION) {
      setStreamDescription('');
    }
  };

  useEffect(() => {
    if (!displayCheckMark) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setDisplayCheckMark(false);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [displayCheckMark]);

  useEffect(
    () => () => {
      const player = playerRef.current;
      if (player) {
        player.dispose();
      }
    },
    []
  );

  if (Object.prototype.hasOwnProperty.call(stream, 'm3u8Url') && stream.m3u8Url !== null) {
    sourceURL = stream.m3u8Url;
    sourceType = 'application/x-mpegURL';
    clippable = '';
  } else if (Object.prototype.hasOwnProperty.call(stream, 'outfile') && stream.outfile.endsWith('mp4')) {
    sourceURL = stream.outfile;
    sourceType = 'video/mp4';
    clippable = (
      <li className={`broadcast-option clip ${clippable}`} data-for="broadcaststreams-tooltip" data-tip="Clip">
        <button
          type="button"
          onClick={handleClippingRequest}
          data-recording-id={stream.id}
          data-source-duration={roundRecordingDuration(stream.startedAt, stream.stoppedAt)}
          data-source-poster={stream.thumbnail}
          data-source-type={sourceType}
          data-source-url={stream.outfile}
          title="Clip"
          aria-label="Clip video"
        />
      </li>
    );
  } else {
    // eslint-disable-next-line no-console
    console.error('No stream or incompatible stream found.');
  }

  if (
    sessionStorage.getItem('currentAccountRole') !== 'ADMIN' &&
    parseInt(sessionStorage.getItem('userId'), 10) !== stream.user.id
  ) {
    allowContentEditing = 'false';
    forbidDeletion = true;
    deleteTooltipCopy = 'No permission to delete';
  } else {
    allowContentEditing = 'true';
    forbidDeletion = false;
    deleteTooltipCopy = 'Delete';
  }

  return (
    <div className="stream">
      <div className="column-spacer">
        <div className="stream-player-container">
          <video
            id={stream.id}
            className="video-js"
            onClick={handleOnClickToPlay}
            preload="none"
            poster={thumbnail}
            onContextMenu={disableRightClickFunc}
          >
            <source src={sourceURL} type={sourceType} />
          </video>
          {showDuration && (
            <div className="stream-duration">{moment.duration(stream.durationSeconds, 'seconds').format()}</div>
          )}
        </div>
      </div>
      <div className="right-column">
        <span className="owner byline">{title}</span>
        <br />
        <span className="started-at byline">
          {`${stream.user.name} ${userRoleType}`} - {moment(stream.startedAt).format('MMM Do YYYY, h:mm a')}
        </span>
        <br />
        &#8203;
        <span
          className="stream-description"
          contentEditable={allowContentEditing}
          data-streamid={stream.id}
          data-streamdescription={streamDescription}
          onBlur={onHandleDescriptionChange}
          onKeyDown={handleEnterKey}
          onClick={handleOnClick}
          suppressContentEditableWarning
          role="textbox"
          tabIndex={0}
        >
          {streamDescription}
        </span>
        &#8203;
        {displayCheckMark && <span className="description-success-checkmark active" />}
        <div className="options-menu">
          <ul className="broadcast-options">
            <ShareButton videoId={stream.id} videoType="STREAM" duration={stream.durationSeconds} />
            {isClippable && (
              <DownloadButtonPicker
                type="broadcaststreams"
                namingConvention={isCurrentUserStreamOwner ? RecordingSource.HOST : RecordingSource.GUEST}
                newTitle={newBroadcastTitle}
                downloadFile={stream}
                buttonId={`btn-${index}`}
                pagePath={pagePath}
              />
            )}

            {!isClippable && (
              <li
                className="broadcast-option download"
                data-for="broadcaststreams-tooltip"
                data-tip="This video recording cannot be downloaded"
              >
                <button type="button" disabled aria-label="Download" />
              </li>
            )}
            {clippable}
            <li className="broadcast-option delete" data-for="broadcaststreams-tooltip" data-tip={deleteTooltipCopy}>
              <button
                type="button"
                className="bin-icon"
                disabled={forbidDeletion}
                onClick={requestDeleteRecording}
                data-recording-id={stream.id}
                data-item-index={index}
                aria-label="Delete video"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

StreamListItem.propTypes = {
  handleClippingRequest: PropTypes.func.isRequired,
  handleDescriptionChange: PropTypes.func.isRequired,
  handleEnterKey: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isCurrentUserStreamOwner: PropTypes.bool.isRequired,
  newBroadcastTitle: PropTypes.string.isRequired,
  stream: heroStreamShape.isRequired,
  streamToken: remoteStreamShape.isRequired,
  requestDeleteRecording: PropTypes.func.isRequired,
  broadcastHash: PropTypes.string.isRequired,
  broadcastId: PropTypes.number.isRequired,
};

momentDurationFormatSetup(moment);

class LegacyBroadcastStreams extends Component {
  constructor(props) {
    super(props);
    this.state = {
      streams: this.props.streams,
      clippingModalIsOpen: false,
      confirmationModalIsOpen: false,
      recordingToDelete: null,
      recordingToDeleteIndex: '',
      clippingSourceId: '',
      clippingSourceDuration: '',
      clippingSourcePoster: '',
      clippingSourceType: '',
      clippingSourceURL: '',
    };

    this.handleClippingRequest = this.handleClippingRequest.bind(this);
    this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.requestDeleteRecording = this.requestDeleteRecording.bind(this);
    this.deleteRecording = this.deleteRecording.bind(this);
  }

  handleClippingRequest(event) {
    const targetData = event.target.dataset;

    this.setState(
      {
        clippingSourceId: targetData.recordingId,
        clippingSourceDuration: targetData.sourceDuration,
        clippingSourcePoster: targetData.sourcePoster,
        clippingSourceType: targetData.sourceType,
        clippingSourceURL: targetData.sourceUrl,
      },
      () => {
        this.openModal('clippingmodal');
      }
    );
  }

  handleStreamInfoChange(params, streamId) {
    return fetch(`${this.props.APIBaseURL}/streams/${streamId}`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'streams handleStreamInfoChange',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from handleStreamInfoChange (BroadcastStreams). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((data) => {
        const { streams } = this.state;
        const newStreams = streams.map((stream) => {
          if (stream.id === streamId) {
            return {
              ...stream,
              description: data.description,
            };
          }

          return stream;
        });
        this.setState({ streams: newStreams });
        this.props.updateStreams(newStreams);
        return data;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', error);
        return { error: error.message };
      });
  }

  handleDescriptionChange(description, streamId) {
    const params = {
      method: 'PATCH',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        description,
      }),
    };

    return this.handleStreamInfoChange(params, streamId);
  }

  handleEnterKey(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      event.target.blur();
    }
  }

  hideDuration(event) {
    const parentDiv = event.target.closest('.stream-player-container');
    parentDiv.className += ' hide-duration';
  }

  openModal(modalName) {
    if (modalName === 'yousure') {
      this.setState({ confirmationModalIsOpen: true });
    } else if (modalName === 'clippingmodal') {
      this.setState({ clippingModalIsOpen: true });
    }
  }

  closeModal() {
    this.setState({ confirmationModalIsOpen: false, clippingModalIsOpen: false });
  }

  requestDeleteRecording(event) {
    const id = event.target.dataset.recordingId;
    const index = event.target.dataset.itemIndex;

    this.setState({ recordingToDelete: id, recordingToDeleteIndex: index }, () => {
      this.openModal('yousure');
    });
    // this.setState({recordingToDelete: recordingId})
    // this.showOptions(recordingId, itemIndex);
  }

  deleteRecording() {
    const newStreams = [...this.state.streams];
    const params = {
      method: 'delete',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
    };

    fetch(`${this.props.APIBaseURL}/stream/delete/${this.state.recordingToDelete}`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'deleteRecording (BroadcastStreams)',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from deleteRecording (BroadcastStreams). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
        }
      })
      .then(() => {
        this.props.reduceStreamCount();
        newStreams.splice(this.state.recordingToDeleteIndex, 1);
        this.setState({ streams: newStreams });
        this.setState({ confirmationModalIsOpen: false });
        this.props.updateStreams(newStreams);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('delete recording API call failed. ', error);
      });
  }

  showDuration(event) {
    const parentDiv = event.target.closest('.stream-player-container');
    const targetClass = parentDiv.className;

    parentDiv.className = targetClass.replace(' hide-duration', '');
  }

  render() {
    if (this.state.streams.length > 0) {
      return (
        <div className="content-container broadcast-streams-content-container legacy">
          {this.state.streams.map((stream, index) => (
            <StreamListItem
              key={stream.id}
              broadcastHash={this.props.broadcast.hash}
              broadcastId={this.props.broadcast.id}
              index={index}
              stream={stream}
              streamToken={this.props.broadcast.remoteStream}
              newBroadcastTitle={this.props.newBroadcastTitle}
              isCurrentUserStreamOwner={stream.user.id === this.props.broadcast.user.id}
              handleClippingRequest={this.handleClippingRequest}
              handleDescriptionChange={this.handleDescriptionChange}
              handleEnterKey={this.handleEnterKey}
              requestDeleteRecording={this.requestDeleteRecording}
            />
          ))}
          <ConfirmationModal
            isOpen={this.state.confirmationModalIsOpen}
            handleOnClose={() => this.closeModal('yousure')}
            handleOnConfirm={this.deleteRecording}
            resourceDescription="Do you really want to delete this recording? This cannot be undone."
          />

          <Modal
            isOpen={this.state.clippingModalIsOpen}
            onRequestClose={() => this.closeModal('clippingmodal')}
            style={customClippingStyles}
          >
            <ClipRecording
              APIBaseURL={this.props.APIBaseURL}
              broadcastTitle={this.props.broadcast.name}
              clipVidDuration={this.state.clippingSourceDuration}
              clipVidPoster={this.state.clippingSourcePoster}
              clipVidType={this.state.clippingSourceType}
              clipVidURL={this.state.clippingSourceURL}
              closeModal={this.closeModal}
              location={this.props.location}
              reAuth={this.props.reAuth}
              sourceId={this.state.clippingSourceId}
              trackEvent={this.props.trackEvent}
            />
          </Modal>
          <ReactTooltip id="broadcaststreams-tooltip" effect="solid" type="light" border />
        </div>
      );
    }

    return (
      <div className="content-container broadcast-streams-content-container">
        <ul>
          <li className="not-found" key="NoPastBroadcasts">
            <span>No other recordings available for this broadcast.</span>
          </li>
        </ul>
      </div>
    );
  }
}

LegacyBroadcastStreams.propTypes = {
  APIBaseURL: PropTypes.string.isRequired,
  broadcast: broadcastStreamShape.isRequired,
  location: routerLocationShape.isRequired,
  newBroadcastTitle: PropTypes.string.isRequired,
  streams: PropTypes.arrayOf(heroStreamShape).isRequired,
  reAuth: PropTypes.func.isRequired,
  reduceStreamCount: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  updateStreams: PropTypes.func.isRequired,
};

export default withRouter(LegacyBroadcastStreams);
